import React, { useState, useRef, useEffect, lazy, Suspense } from 'react';
import Button from '../../composants/Button/Button';
import TabNav from '../../composants/TabNav/TabNav';
import ChangeRole from '../../composants/ChangeRole/ChangeRole';
import MesDocuments from '../../composants/MesDocuments/MesDocuments';
import GestionComptes from '../../composants/GestionComptes/GestionComptes';
import GestionComptesPrestataires from '../../composants/GestionComptesPrestataires/GestionComptesPrestataires';
import { useAuth } from '../../contexts/AuthContext'; // Ajout de l'import pour accéder au contexte d'authentification
import {
  isClientRole,
  isPrestataireRole,
  isAdminRole
} from '../../utils/roleUtils'; // Import des fonctions de utils/roleUtils
import './Profil.css';
import ModificationModal from '../../composants/ModificationModal/ModificationModal';

const TABS = [
  "Présentation",
  "Droits",
  "Documents",
  "Coordonées"
];

const DOCUMENT_TABS = [
  "Logo entreprise",
  "Pièces du marché",
  "Assurance",
  "Attestation fiscale et sociale",
  "Certifications et attestations"
];

type ContactKeys = 'nom' | 'prenom' | 'telephone' | 'fonction';

type OrganismeKeys = 'nom' | 'siret' | 'adresse' | 'ville' | 'codePostal';

interface IgnSuggestion {
  label: string;
  houseNumber?: string;
  street?: string;
  postcode?: string;
  city?: string;
}

interface Contact {
  nom: string;
  prenom: string;
  email: string;
  telephone: string;       // Téléphone de contact
  fonction: string; // Nouvelle propriété
  isEditing: boolean;
  role: string;
}

interface Organisme {
  nom: string;
  siret: string;
  adresse: string;
  ville: string;
  codePostal: string;
  numeroVoie: string;
  typeVoie: string;
  nomVoie: string;
  departement: string;
  isEditing: boolean;
  id?: string | number; // <-- ajout
}

interface ProfileData {
  logo: string;
  entreprise: string;
  contact: Contact;
  organisme: Organisme;
}

const Profil: React.FC = () => {
  // Récupération des informations utilisateur depuis le contexte d'authentification
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<number>(0);
  const [activeDocTab, setActiveDocTab] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [phoneConnexionError, setPhoneConnexionError] = useState<string | null>(null);
  const [siretError, setSiretError] = useState<string | null>(null);
  const [showModificationModal, setShowModificationModal] = useState<boolean>(false);
  const [dataLoaded, setDataLoaded] = useState<boolean>(false); // Cache flag

  // États pour l'autocomplétion d'adresse IGN
  const [adresseInput, setAdresseInput] = useState('');
  const [adresseSuggestions, setAdresseSuggestions] = useState<IgnSuggestion[]>([]);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const [modificationData, setModificationData] = useState({
    nom: '',
    prenom: '',
    fonction: '',
    telephone: '',
    email: ''
  });

  // Données du profil
  const [profileData, setProfileData] = useState<ProfileData>({
    logo: '',
    entreprise: "Nom de l'entreprise",
    contact: {
      nom: '',
      prenom: '',
      email: '',
      telephone: '',
      fonction: '',
      isEditing: false,
      role: user?.role || '' // Utiliser directement le rôle de l'utilisateur connecté
    },
    organisme: {
      nom: '',
      siret: '',
      adresse: '',
      ville: '',
      codePostal: '',
      numeroVoie: '',
      typeVoie: '',
      nomVoie: '',
      departement: '',
      isEditing: false,
      id: undefined
    }
  });

  // Mémoïsation de la fonction getContactUpdateRoute
  const getContactUpdateRoute = React.useMemo(() => {
    return (role: string) => {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      switch (role) {
        case 'Acheteur signataire admin':
          return `${apiUrl}/responsables-achat/me`;
        case 'Acheteur signataire':
          return `${apiUrl}/signataires/me`;
        case 'Acheteur':
          return `${apiUrl}/acheteurs/me`;
        case 'Prestataire signataire admin':
          return `${apiUrl}/gestionnaires/me`;
        case 'Prestataire signataire':
          return `${apiUrl}/signataires/me`;
        case 'Prestataire':
          return `${apiUrl}/interlocuteurs/me`;
        default:
          return '';
      }
    };
  }, []);

  // Nos propres implémentations de isClientRole et isPrestataireRole (déplacées avant useEffect)
  const isClientRoleLocal = React.useCallback((role: string | undefined): boolean => {
    if (!role) return false;
    return ['Acheteur', 'Acheteur signataire', 'Acheteur signataire admin'].includes(role);
  }, []);

  const isPrestataireRoleLocal = React.useCallback((role: string | undefined): boolean => {
    if (!role) return false;
    return ['Prestataire', 'Prestataire signataire', 'Prestataire signataire admin'].includes(role);
  }, []);

  // Charger les données de l'utilisateur depuis l'API - OPTIMISÉ
  useEffect(() => {
    // Ne charger qu'une seule fois
    if (dataLoaded) return;

    const fetchUserData = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('Aucun token d\'authentification trouvé');

        const apiUrl = process.env.REACT_APP_API_URL || '';
        const userRoute = getContactUpdateRoute(user?.role || '');

        // OPTIMISATION : Paralléliser les appels API au lieu de les faire séquentiellement
        const requests: Promise<Response>[] = [
          // 1. Récupérer les infos de connexion via /me
          fetch(`${apiUrl}/me`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          })
        ];

        // 2. Ajouter la requête utilisateur métier si la route existe
        if (userRoute) {
          requests.push(
            fetch(userRoute, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              }
            })
          );
        }

        // Exécuter toutes les requêtes en parallèle
        const responses = await Promise.all(requests);

        if (!responses[0].ok) throw new Error('Erreur lors de la récupération des informations de connexion');

        const [meDataRaw, userDataRaw] = await Promise.all([
          responses[0].json(),
          responses[1]?.ok ? responses[1].json() : Promise.resolve({ data: {} })
        ]);

        const meData = meDataRaw.data;
        const userData = userDataRaw.data || {};

        // Pour les prestataires, c'est id_entite, pour les acheteurs, c'est id_organisme
        const idOrganisme = userData?.id_organisme || userData?.id_entite;

        let organismeData = {
          nom: '',
          siret: '',
          adresse: '',
          ville: '',
          codePostal: '',
          numeroVoie: '',
          typeVoie: '',
          nomVoie: '',
          departement: '',
          isEditing: false,
          id: undefined as string | number | undefined
        };

        // Récupérer nom, siret et id selon le rôle
        if (isPrestataireRoleLocal(user?.role)) {
          if (userData.entite) {
            organismeData.nom = userData.entite.nom || '';
            organismeData.id = userData.entite.id;
            if (userData.entite.entreprises && userData.entite.entreprises.length > 0) {
              organismeData.siret = String(userData.entite.entreprises[0].siret) || '';
            }
          }
        } else {
          if (userData.organisme) {
            organismeData.nom = userData.organisme.nom || '';
            organismeData.siret = userData.organisme.siret || '';
            organismeData.id = userData.organisme.id;
          }
        }

        // 3. Récupérer adresse uniquement si nécessaire
        if (idOrganisme) {
          const routeType = isPrestataireRoleLocal(user?.role) ? 'entites' : 'organismes';
          const orgResponse = await fetch(`${apiUrl}/${routeType}/${idOrganisme}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          });

          if (orgResponse.ok) {
            const orgData = await orgResponse.json();
            if (orgData.status === 'success' && orgData.data) {
              const adresseObj = orgData.data.adresse;

              // Nettoyer nom_voie si elle commence par le numéro de voie
              let nomVoieClean = adresseObj?.nom_voie || '';
              const numeroVoie = adresseObj?.numero_voie || '';
              if (numeroVoie && nomVoieClean.startsWith(numeroVoie + ' ')) {
                nomVoieClean = nomVoieClean.substring((numeroVoie + ' ').length);
              }

              organismeData.adresse = adresseObj
                ? `${adresseObj.numero_voie || ''} ${adresseObj.type_voie || ''} ${nomVoieClean}${adresseObj.arrondissement ? ', ' + adresseObj.arrondissement : ''}`
                : '';
              organismeData.ville = adresseObj?.ville || '';
              organismeData.codePostal = adresseObj?.code_postal || '';
              organismeData.numeroVoie = numeroVoie;
              organismeData.typeVoie = adresseObj?.type_voie || '';
              organismeData.nomVoie = nomVoieClean;
              organismeData.departement = adresseObj?.departement || '';

              // Initialiser adresseInput avec l'adresse complète
              const adresseComplete = [
                numeroVoie,
                adresseObj?.type_voie,
                nomVoieClean,
                adresseObj?.code_postal,
                adresseObj?.ville
              ].filter(Boolean).join(' ');
              setAdresseInput(adresseComplete);
            }
          }
        }

        setProfileData(prev => ({
          ...prev,
          contact: {
            ...prev.contact,
            nom: userData?.nom || prev.contact.nom,
            prenom: userData?.prenom || prev.contact.prenom,
            email: meData.email || '',
            role: user?.role || '',
            telephone: userData?.telephone || prev.contact.telephone,
            fonction: userData?.fonction || prev.contact.fonction
          },
          organisme: organismeData
        }));

        setDataLoaded(true); // Marquer les données comme chargées

      } catch (error) {
        console.error('Erreur lors de la récupération des informations utilisateur:', error);
        setError('Impossible de récupérer les informations utilisateur.');
      } finally {
        setLoading(false);
      }
    };

    if (user?.role) {
      fetchUserData();
    }
  }, [user?.role, dataLoaded, getContactUpdateRoute, isClientRoleLocal, isPrestataireRoleLocal]);

  // Effet pour l'autocomplétion d'adresse IGN
  useEffect(() => {
    if (adresseInput.trim().length < 3) {
      setAdresseSuggestions([]);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      const controller = new AbortController();

      fetch(`https://data.geopf.fr/geocodage/completion?text=${encodeURIComponent(adresseInput)}&index=StreetAddress&limit=8`,
        { signal: controller.signal })
        .then(r => r.ok ? r.json() : Promise.reject(r))
        .then(json => {
          console.log('IGN completion', json);
          if (json && json.results) {
            // Transformation des résultats dans le format IgnSuggestion
            const suggestions = json.results.map((result: any) => ({
              label: result.fulltext,
              houseNumber: result.kind === 'housenumber' ? result.fulltext.split(' ')[0] : '',
              street: result.street,
              postcode: result.zipcode,
              city: result.city
            }));
            console.log('Suggestions transformées:', suggestions);
            setAdresseSuggestions(suggestions);
          }
        })
        .catch((error) => {
          if (error.name !== 'AbortError') {
            console.error('Erreur lors de la récupération des adresses:', error);
          }
        });

      return () => controller.abort();
    }, 300);
  }, [adresseInput]);

  // Fonction utilitaire pour détecter le type de voie
  const detectTypeVoie = (street?: string): string => {
    if (!street) return '';
    const s = street.toLowerCase();
    if (s.startsWith('rue')) return 'rue';
    if (s.startsWith('avenue')) return 'avenue';
    if (s.startsWith('boulevard')) return 'boulevard';
    if (s.startsWith('impasse')) return 'impasse';
    if (s.startsWith('allée')) return 'allée';
    if (s.startsWith('place')) return 'place';
    return '';
  };

  // Sélection d'une adresse depuis les suggestions IGN
  const handleSelectAdresse = (suggestion: IgnSuggestion) => {
    const type = detectTypeVoie(suggestion.street);
    let nomVoie = suggestion.street || '';

    // Retirer le type de voie du nom de la voie
    if (type && nomVoie) {
      const typeRegex = new RegExp(`^${type}\\s+`, 'i');
      nomVoie = nomVoie.replace(typeRegex, '');
    }

    setProfileData(prev => ({
      ...prev,
      organisme: {
        ...prev.organisme,
        numeroVoie: suggestion.houseNumber || '',
        nomVoie: nomVoie,
        codePostal: suggestion.postcode || '',
        ville: suggestion.city || '',
        departement: suggestion.postcode ? suggestion.postcode.slice(0, 2) : '',
        typeVoie: type,
        adresse: `${suggestion.houseNumber || ''} ${type} ${nomVoie}`.trim()
      }
    }));

    setAdresseInput(suggestion.label);
    setAdresseSuggestions([]);
  };

  // Navigation rapide retour
  const handleBackToProfil = () => {
    setActiveDocTab(null);
    setActiveTab(0);
  };

  // Fonction pour vérifier si l'utilisateur est autorisé à modifier ses informations
  const canEditInfo = () => {
    const role = user?.role || '';
    // Interlocuteur et Acheteur ne peuvent pas modifier les infos
    if (role === 'Prestataire' || role === 'Acheteur') {
      return false;
    }
    // Les administrateurs (gestionnaires) ne peuvent pas modifier leurs coordonnées
    if (role === 'Acheteur signataire admin' || role === 'Prestataire signataire admin') {
      return false;
    }
    return true;
  };

  // SIMPLE: Acheteur signataire admin peut modifier le bloc Présentation
  const canEditOrganisme = () => {
    return user?.role === 'Acheteur signataire admin';
  };

  // Fonction spécifique pour l'édition du SIRET - Acheteur signataire admin
  const canEditSiret = () => {
    return user?.role === 'Acheteur signataire admin';
  };

  // Fonction pour déterminer le type d'action pour le bouton de modification du profil
  const getProfileActionType = () => {
    const role = user?.role || '';

    if (role === 'Acheteur signataire admin' || role === 'Prestataire signataire admin') {
      return 'Modifier'; // Les admins peuvent faire des passations
    } else if (role === 'Acheteur' || role === 'Prestataire') {
      return 'demande'; // Les simples utilisateurs font des demandes
    } else if (['Acheteur signataire', 'Prestataire signataire'].includes(role)) {
      return 'Modifier'; // Les signataires peuvent aussi faire des passations
    } else {
      return 'demande'; // Par défaut
    }
  };

  // Validation du téléphone - accepte tout numéro à 10 chiffres (mobile ou fixe)
  const validatePhoneNumber = (phoneNumber: string, role: string = user?.role || '') => {
    const cleanNumber = phoneNumber.replace(/\s+/g, '').replace(/[^0-9]/g, '');
    // Accepte n'importe quel numéro de téléphone français à 10 chiffres
    return /^\d{10}$/.test(cleanNumber);
  };

  // Ajouter en haut avec les autres fonctions
  const validateSiret = (siret: string) => {
    // Un SIRET valide contient 14 chiffres
    return /^\d{14}$/.test(siret.replace(/\s/g, ''));
  };

  // Fonction d'initialisation des données de modification
  const initModificationData = () => {
    setModificationData({
      nom: profileData.contact.nom,
      prenom: profileData.contact.prenom,
      fonction: profileData.contact.fonction,
      telephone: profileData.contact.telephone,
      email: profileData.contact.email
    });
  };

  // Fonction d'initialisation des données de modification
  const handleModificationChange = (field: string, value: string) => {
    setModificationData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Fonction pour gérer l'envoi des modifications (fonction existante)
  const handleSubmitModification = async () => {
    // ...
  };

  // Fonction pour mettre à jour les informations de contact (Acheteur signataire admin)
  const handleUpdateContactInfo = async () => {
    if (!user?.role) return;

    // Validation du téléphone
    if (profileData.contact.telephone && !validatePhoneNumber(profileData.contact.telephone, user.role)) {
      setPhoneError("Le numéro doit contenir 10 chiffres");
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token d\'authentification non trouvé');

      const apiUrl = process.env.REACT_APP_API_URL || '';
      const updateRoute = getContactUpdateRoute(user.role);

      const response = await fetch(updateRoute, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          nom: profileData.contact.nom,
          prenom: profileData.contact.prenom,
          telephone: profileData.contact.telephone,
          fonction: profileData.contact.fonction
          // Note: email n'est pas inclus car sa modification est en développement
        })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la mise à jour des informations');
      }

      const data = await response.json();
      if (data.status === 'success') {
        alert('Informations mises à jour avec succès');
        // Recharger les données
        setDataLoaded(false);
      } else {
        throw new Error(data.message || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      alert(`Erreur: ${error instanceof Error ? error.message : 'Une erreur est survenue'}`);
    }
  };

  // Onglet coordonnées
  const renderCoordonnees = () => (
    <>
      <section className="profil-tab-page">
        {loading ? (
          <p className="loading-indicator">Chargement des informations...</p>
        ) : error ? (
          <p className="error-message">{error}</p>
        ) : (
          <>
            {/* Première section: Mes informations de contact */}
            <h2 className='section-header'>Mes informations de contact</h2>
            <div className="section-content">
              <div className="form-group identifiant-connexion">
                <label>Identifiant de connexion</label>
                <div className="identifiant-connexion-input" title="Fonctionnalité en cours de développement">
                  <input
                    type="email"
                    value={profileData.contact.email}
                    className="input-inactif"
                  />
                  <Button
                    value="Modifier"
                    variant="primary"
                    size="small"
                    disabled={true}
                    onClick={() => { }}
                  />
                </div>
              </div>
              <br />
              <hr />
              <br />
              <div className="form-row grid-4">
                <div className="form-group">
                  <label>Nom</label>
                  <input
                    type="text"
                    value={profileData.contact.nom}
                    onChange={e => setProfileData(prev => ({
                      ...prev,
                      contact: { ...prev.contact, nom: e.target.value }
                    }))}
                    disabled={!canEditInfo()}
                    className={!canEditInfo() ? "input-inactif" : ""}
                  />
                </div>

                <div className="form-group">
                  <label>Prénom</label>
                  <input
                    type="text"
                    value={profileData.contact.prenom}
                    onChange={e => setProfileData(prev => ({
                      ...prev,
                      contact: { ...prev.contact, prenom: e.target.value }
                    }))}
                    disabled={!canEditInfo()}
                    className={!canEditInfo() ? "input-inactif" : ""}
                  />
                </div>
                <div className="form-group span-2">
                  <label>Fonction</label>
                  <input
                    type="text"
                    value={profileData.contact.fonction}
                    onChange={e => setProfileData(prev => ({
                      ...prev,
                      contact: { ...prev.contact, fonction: e.target.value }
                    }))}
                    disabled={!canEditInfo()}
                    className={!canEditInfo() ? "input-inactif" : ""}
                  />
                </div>

                <div className="form-group span-2">
                  <label>{isClientRoleLocal(user?.role) ? "Numéro de téléphone acheteur" : "Téléphone"}</label>
                  <input
                    type="text"
                    value={profileData.contact.telephone}
                    onChange={e => {
                      const newPhone = e.target.value;
                      setProfileData(prev => ({
                        ...prev,
                        contact: { ...prev.contact, telephone: newPhone }
                      }));
                      if (newPhone.trim()) {
                        if (!validatePhoneNumber(newPhone, user?.role || '')) {
                          setPhoneError("Le numéro doit contenir 10 chiffres");
                        } else {
                          setPhoneError(null);
                        }
                      } else {
                        setPhoneError(null);
                      }
                    }}
                    disabled={!canEditInfo()}
                    className={phoneError ? "input-error" : (!canEditInfo() ? "input-inactif" : "")}
                  />
                  {phoneError && <p className="error-message">{phoneError}</p>}
                </div>

                <div className="form-group span-2">
                  <label>Email</label>
                  <input
                    type="email"
                    value={profileData.contact.email}
                    disabled={true}
                    className="input-inactif"
                    title="La modification de l'email est en cours de développement"
                  />
                </div>
              </div>

              {/* Message d'information selon le rôle */}
              {user?.role === 'Acheteur signataire admin' || user?.role === 'Prestataire signataire admin' ? (
                <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                  <p className="info-text" style={{ marginBottom: '0.5rem' }}>
                    Note : La modification de l'email (identifiant de connexion) est en cours de développement.
                  </p>
                  <p style={{ fontSize: '0.9rem', color: '#ff6b00', backgroundColor: '#fff3e0', padding: '0.75rem', borderRadius: '4px', border: '1px solid #ffb74d' }}>
                    ℹ️ <strong>Restriction administrateur :</strong> En tant que gestionnaire, vous ne pouvez pas modifier vos coordonnées (nom, prénom, fonction, téléphone). Pour toute modification, veuillez contacter le support ou un autre gestionnaire.
                  </p>
                </div>
              ) : canEditInfo() ? (
                <p className="info-text" style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                  Note : La modification de l'email (identifiant de connexion) est en cours de développement.
                </p>
              ) : (
                <p style={{ marginTop: '1rem', marginBottom: '1rem', fontSize: '0.9rem', color: '#666' }}>
                  Pour toute modification de vos informations, veuillez contacter votre administrateur.
                </p>
              )}

              {/* Bouton de mise à jour pour les informations de contact */}
              {user?.role === 'Acheteur signataire admin' || user?.role === 'Prestataire signataire admin' ? (
                <div className="button-container">
                  <Button
                    value="Modification restreinte"
                    variant="secondary"
                    size="small"
                    disabled={true}
                    onClick={() => { }}
                  />
                </div>
              ) : canEditInfo() ? (
                <div className="button-container">
                  <Button
                    value="Modifier"
                    variant="primary"
                    size="small"
                    disabled={false}
                    onClick={handleUpdateContactInfo}
                  />
                </div>
              ) : (
                <div className="button-container">
                  <Button
                    value="Faire une demande"
                    variant="primary"
                    size="small"
                    disabled={true}
                    onClick={() => { }}
                  />
                </div>
              )}
            </div>
            {/* Deuxième section: Mes informations de connexion */}
            <section className="profil-tab-page">

              {/* Bouton pour envoyer une demande de modification - visible pour tous */}

            </section>

            {/* Section Coordonnées d'entreprise reste inchangée */}
            <section className="profil-tab-page">
              <h2 className='section-header'>Présentation</h2>
              <div className="section-content">
                <div className="form-row grid-2">
                  {/* Nom de l'organisme sur une colonne */}
                  <div className="form-group">
                    <label>Nom de l'organisme</label>
                    <input
                      type="text"
                      value={profileData.organisme?.nom || ''} // Ajout du "?" et valeur par défaut
                      onChange={e => setProfileData(prev => ({
                        ...prev,
                        organisme: { ...(prev.organisme || {}), nom: e.target.value }
                      }))}
                      disabled={!canEditOrganisme()}
                      className={!canEditOrganisme() ? "input-inactif" : ""}
                    />
                  </div>

                  {/* SIRET sur une colonne - éditable uniquement pour Acheteur signataire admin */}
                  <div className="form-group">
                    <label>Siret</label>
                    <input
                      type="text"
                      value={profileData.organisme.siret}
                      onChange={e => {
                        const value = e.target.value;
                        setProfileData(prev => ({
                          ...prev,
                          organisme: { ...prev.organisme, siret: value }
                        }));
                        // Valider le SIRET
                        if (value && !validateSiret(value)) {
                          setSiretError('Le SIRET doit contenir exactement 14 chiffres');
                        } else {
                          setSiretError(null);
                        }
                      }}
                      disabled={!canEditSiret()}
                      className={!canEditSiret() ? "input-inactif" : ""}
                      placeholder="14 chiffres"
                      maxLength={14}
                    />
                    {siretError && <div className="error-message">{siretError}</div>}
                  </div>

                  {/* Adresse sur deux colonnes avec autocomplétion IGN */}
                  <div className="form-group span-2" style={{ position: 'relative' }}>
                    <label>Adresse</label>
                    <input
                      type="text"
                      value={adresseInput}
                      onChange={e => {
                        const newValue = e.target.value;
                        setAdresseInput(newValue);
                        setProfileData(prev => ({
                          ...prev,
                          organisme: { ...prev.organisme, adresse: newValue }
                        }));
                      }}
                      onFocus={() => {
                        if (canEditOrganisme()) {
                          setProfileData(prev => ({
                            ...prev,
                            organisme: { ...prev.organisme, isEditing: true }
                          }));
                        }
                      }}
                      onBlur={() => {
                        setTimeout(() => {
                          setProfileData(prev => ({
                            ...prev,
                            organisme: { ...prev.organisme, isEditing: false }
                          }));
                          setAdresseSuggestions([]);
                        }, 200);
                      }}
                      disabled={!canEditOrganisme()}
                      className={!canEditOrganisme() ? "input-inactif" : ""}
                      placeholder="Commencez à taper une adresse..."
                      autoComplete="off"
                    />
                    {adresseSuggestions.length > 0 && profileData.organisme.isEditing && (
                      <ul className="autocomplete-suggestions" style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        listStyle: 'none',
                        padding: 0,
                        margin: '4px 0 0 0',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                      }}>
                        {adresseSuggestions.map((suggestion, index) => (
                          <li
                            key={index}
                            onClick={() => handleSelectAdresse(suggestion)}
                            style={{
                              padding: '10px 15px',
                              cursor: 'pointer',
                              borderBottom: index < adresseSuggestions.length - 1 ? '1px solid #eee' : 'none'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                          >
                            {suggestion.label}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* Code postal sur une colonne */}
                  <div className="form-group">
                    <label>Code Postal</label>
                    <input
                      type="text"
                      value={profileData.organisme.codePostal}
                      onChange={e => setProfileData(prev => ({
                        ...prev,
                        organisme: { ...prev.organisme, codePostal: e.target.value }
                      }))}
                      disabled={!canEditOrganisme()}
                      className={!canEditOrganisme() ? "input-inactif" : ""}
                    />
                  </div>

                  {/* Ville sur une colonne */}
                  <div className="form-group">
                    <label>Ville</label>
                    <input
                      type="text"
                      value={profileData.organisme.ville}
                      onChange={e => setProfileData(prev => ({
                        ...prev,
                        organisme: { ...prev.organisme, ville: e.target.value }
                      }))}
                      disabled={!canEditOrganisme()}
                      className={!canEditOrganisme() ? "input-inactif" : ""}
                    />
                  </div>
                </div>

                {/* Bouton de mise à jour des infos d'entreprise */}
                {canEditOrganisme() ? (
                  <div className="button-container">
                    <Button
                      value="Mettre à jour"
                      variant="primary"
                      size="small"
                      onClick={() => {
                        const updateOrganismeInfo = async () => {
                          try {
                            // Valider le SIRET si l'utilisateur peut l'éditer
                            if (canEditSiret() && profileData.organisme.siret) {
                              if (!validateSiret(profileData.organisme.siret)) {
                                alert('Le SIRET doit contenir exactement 14 chiffres');
                                return;
                              }
                            }

                            const token = localStorage.getItem('token');
                            const id = profileData.organisme.id;
                            if (!id) {
                              alert("Impossible de déterminer l'organisme à mettre à jour.");
                              return;
                            }

                            const apiUrl = process.env.REACT_APP_API_URL || '';
                            const routeType = isPrestataireRoleLocal(user?.role) ? 'entites' : 'organismes';

                            // Construire le body avec ou sans SIRET selon les permissions
                            const bodyData: any = {
                              nom: profileData.organisme.nom,
                              numero_voie: profileData.organisme.numeroVoie,
                              type_voie: profileData.organisme.typeVoie,
                              nom_voie: profileData.organisme.nomVoie,
                              ville: profileData.organisme.ville,
                              code_postal: profileData.organisme.codePostal,
                              departement: profileData.organisme.departement
                            };

                            // Ajouter le SIRET uniquement si l'utilisateur est Acheteur signataire admin
                            if (canEditSiret()) {
                              bodyData.siret = profileData.organisme.siret;
                            }

                            const response = await fetch(`${apiUrl}/${routeType}/${id}`, {
                              method: 'PUT',
                              headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                              },
                              body: JSON.stringify(bodyData)
                            });

                            if (!response.ok) {
                              const errorData = await response.json().catch(() => ({}));
                              throw new Error(errorData.message || 'Erreur lors de la mise à jour des informations d\'entreprise');
                            }

                            alert('Vous avez bien modifié vos informations');

                            // Recharger les données depuis l'API
                            setDataLoaded(false);
                            window.location.reload();
                          } catch (error) {
                            console.error('Erreur:', error);
                            alert('Une erreur est survenue lors de la mise à jour des informations');
                          }
                        };
                        updateOrganismeInfo();
                      }}
                    />
                  </div>
                ) : (
                  <p className="info-text">
                    {isPrestataireRoleLocal(user?.role)
                      ? "Pour des raisons contractuelles (engagement de 2 ans), les informations de l'entreprise ne peuvent pas être modifiées. Veuillez contacter le support pour toute demande de changement."
                      : isClientRoleLocal(user?.role) && user?.role !== 'Acheteur signataire admin'
                        ? "Pour tout changement des informations d'entreprise, contactez l’administrateur de votre compte."
                        : "Pour tout changement des informations d'entreprise, contactez l’administrateur de votre compte."
                    }
                  </p>
                )}
              </div>
            </section>
          </>
        )}
      </section>


    </>
  );

  // Onglet Gestion d'équipe - Affiche 2 sections pour prestataires, 1 section pour acheteurs
  const renderListeCoordonnees = () => {
    const isPrestataire = isPrestataireRoleLocal(user?.role);

    if (isPrestataire) {
      // Pour les prestataires : 2 sections (Affectation + Membres de l'équipe)
      return (

        <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 200px)', gap: '20px' }}>

          {/* Section 2: Membres de l'équipe */}
          <section className="profil-tab-page" style={{ flex: '1', minHeight: 0 }}>
            <h2 className='section-header'>Membres de l'équipe</h2>
            <div className="section-content section-content-scrollable">
              <React.Suspense fallback={<div>Chargement...</div>}>
                <GestionComptesPrestataires />
              </React.Suspense>
            </div>
          </section>


          {/* Section 1: Affectation des membres sur lot remportés */}
          <section className="profil-tab-page" style={{ flex: '1', minHeight: 0 }}>
            <h2 className='section-header'>Affectation des membres sur les lots remportés</h2>
            <div className="section-content section-content-scrollable">
              <GestionComptes />
            </div>
          </section>


        </div>
      );
    } else {
      // Pour les acheteurs : 1 seule section (Membres de l'équipe avec gestion des comptes)
      return (
        <section className="profil-tab-page">
          <h2 className='section-header'>Membres de l'équipe</h2>
          <div className="section-content">
            <React.Suspense fallback={<div>Chargement...</div>}>
              <GestionComptes userType="acheteur" />
            </React.Suspense>
          </div>
        </section>
      );
    }
  };

  // Onglet droits utilisateur
  const renderDroitsUtilisateur = () => (
    <ChangeRole organismeId={Number(profileData.organisme.id) || 0} />
  );

  // Onglet mes documents
  const renderMesDocuments = () => (
    <div className="profil-documents-page">
      <MesDocuments />
    </div>
  );

  // Onglet gestion des comptes - RENOMMÉ en "Mes interlocuteurs" pour Prestataire signataire admin
  const renderMesInterlocuteurs = () => {
    return <GestionComptes />;
  };

  // Déterminer quels onglets sont visibles selon le rôle - MÉMOÏSÉ
  const getVisibleTabs = React.useMemo(() => {
    const userRole = user?.role;
    // console.log(userRole);

    // Onglets de base disponibles pour tous les utilisateurs
    const baseTabs = ["Présentation"];

    // Ajouter l'onglet "Droits" pour tous les acheteurs
    if (isClientRoleLocal(userRole)) {
      baseTabs.push("Droits");
    }

    // Ajouter l'onglet "Gestion d'équipe" pour tous les acheteurs
    if (isClientRoleLocal(userRole)) {
      baseTabs.push("Gestion d'équipe");
    }

    // Ajouter l'onglet "Mes documents" pour tous les prestataires
    if (isPrestataireRoleLocal(userRole)) {
      baseTabs.push("Documents");
    }

    // Ajouter "Gestion d'équipe" pour tous les prestataires
    if (isPrestataireRoleLocal(userRole)) {
      baseTabs.push("Gestion d'équipe");
    }

    // NOTE: L'onglet "Mes interlocuteurs" a été fusionné avec "Gestion d'équipe"
    // Il n'est plus nécessaire en tant que tab séparée

    // Ajouter l'onglet "Mon besoin" pour les rôles clients
    if (isClientRoleLocal(userRole)) {
      baseTabs.push("Mon besoin");
    }

    // Ajouter l'onglet "Mes évaluations" pour les rôles prestataire spécifiques
    if (isPrestataireRoleLocal(userRole)) {
      baseTabs.push("Mes évaluations");
    }

    baseTabs.push("Statistiques");

    return baseTabs;
  }, [user?.role, isClientRoleLocal, isPrestataireRoleLocal]);

  // Obtenir les onglets visibles
  const visibleTabs = getVisibleTabs;

  // Restaurer l'onglet actif depuis le localStorage après rechargement (pour réattribution)
  useEffect(() => {
    const savedTab = localStorage.getItem('activeProfilTab');
    if (savedTab) {
      const tabIndex = visibleTabs.indexOf(savedTab);
      if (tabIndex !== -1) {
        setActiveTab(tabIndex);
      }
      // Nettoyer le localStorage après avoir restauré l'onglet
      localStorage.removeItem('activeProfilTab');
    }
  }, [visibleTabs]);

  // Déterminer quels onglets doivent être inactifs - MÉMOÏSÉ
  const getInactiveTabs = React.useMemo(() => {
    const inactiveTabs: number[] = [];

    // L'onglet "Mon besoin" est inactif pour le moment
    const monBesoinIndex = visibleTabs.indexOf("Mon besoin");
    if (monBesoinIndex !== -1) {
      inactiveTabs.push(monBesoinIndex);
    }

    // L'onglet "Mes évaluations" est aussi inactif pour le moment
    const mesEvaluationsIndex = visibleTabs.indexOf("Mes évaluations");
    if (mesEvaluationsIndex !== -1) {
      inactiveTabs.push(mesEvaluationsIndex);
    }

    const statsIndex = visibleTabs.indexOf("Statistiques");
    if (statsIndex !== -1) {
      inactiveTabs.push(statsIndex);
    }

    return inactiveTabs;
  }, [visibleTabs]);

  // Obtenir les onglets inactifs
  const inactiveTabs = getInactiveTabs;

  // Gérer le changement d'onglet avec index ajustés
  const handleTabChange = (tabIdx: number) => {
    // Stocker l'index réel dans le state
    setActiveTab(tabIdx);
    setActiveDocTab(null);
  };

  const renderMonBesoin = () => (
    <div className="profil-tab-page">
      <div className="coming-soon-message">
        <h2>Fonctionnalité à venir</h2>
        <p>Cette fonctionnalité sera disponible prochainement. Vous pourrez alors définir et gérer vos besoins spécifiques.</p>
      </div>
    </div>
  );

  // Déterminer quel contenu afficher selon l'onglet actif - MÉMOÏSÉ
  const renderTabContent = React.useMemo(() => {
    switch (visibleTabs[activeTab]) {
      case "Présentation":
        return renderCoordonnees();
      case "Droits":
        return renderDroitsUtilisateur();
      case "Documents":
        return renderMesDocuments();
      case "Gestion d'équipe":
        return renderListeCoordonnees(); // Affiche maintenant GestionComptes
      case "Mon besoin":
        return renderMonBesoin();
      default:
        return null;
    }
  }, [visibleTabs, activeTab, profileData, loading, error, phoneError, user, showModificationModal, modificationData]);

  const monBesoinMessage = "Cette fonctionnalité sera disponible prochainement";

  // Page principale avec onglets toujours visibles
  return (
    <div className="profil-container">
      <h1>Profil</h1>
      <div className="profil-tabs">
        <TabNav
          tabs={visibleTabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          inactiveTabs={inactiveTabs}
          inactiveTabsTooltips={{
            "Mon besoin": monBesoinMessage
          }}
        />
      </div>
      {renderTabContent}
      <ModificationModal
        showModal={showModificationModal}
        data={modificationData}
        onChange={handleModificationChange}
        onClose={() => setShowModificationModal(false)}
        onSubmit={handleSubmitModification}
        userRole={user?.role}
        phoneError={phoneError}
        setPhoneError={setPhoneError}
        validatePhoneNumber={validatePhoneNumber} // Ajout de cette prop
      />
    </div>
  );
};

export default Profil;